import constants from "../../../../appConstants";
import {
  roundingNumbers,
  mergeUserConfig,
  setTimeFromDateTime,
  suppressEnter,
  customNumericComparator,
  formatTime,
  suppressKeyboardEventHandler,
} from '@dataGrid/gridUtilities';
import { routeNameCellStyling } from "@dataGrid/cellStyling";
import {
  MultiSelectFilter,
  DateTimeFilter,
  StaticFilter,
} from '@dataGrid/components/columnFilters';
import {
  TextFieldEditor,
  DateTimeEditor,
  DropdownEditor,
} from '@dataGrid/components/cellEditors';
import {
  LockStatusRenderer,
  DotRenderer,
  DropdownValueCellRenderer,
} from '@dataGrid/components/cellRenderer';
import ReturnToTerminalIcon from "../Common/ReturnToTerminalIcon";
import EtaEtdRenderer from "./EtaEtdRenderer";

export default class GridConfig {
  constructor(configuration) {
    const config = configuration || {};
    this.name = "GridConfiguration";
    this.config = GridConfig.getConfiguration(config);
  }
  static getConfiguration({
    userConfig,
    featureFlags,
    ROUTENAME_EDITOR_PARAMS,
  }) {
    const defaultConfig = GridConfig.getDefaultConfiguration(
      featureFlags,
      ROUTENAME_EDITOR_PARAMS
    );
    return mergeUserConfig({
      defaultConfig,
      userConfig,
    });
  }

  static isNotUnrtdRoute(params) {
    return params.data.routeName != constants.UNRTD_ROUTENAME;
  }
  static isNotUnrtdOrTrapRoute(params) {
    return (
      params.data.routeName != constants.UNRTD_ROUTENAME &&
      !params.data.isTrapRoute
    );
  }

  static minutesToHours = params => {
    if (params && params.data.totalTime !== null && params.data.totalTime !== undefined) {
        const minutes = parseInt(params.data.totalTime);
        const hour = Math.floor(minutes / 60);
        const min = minutes % 60;
        return `${hour.toString().padStart(2, '0')}:${min.toString().padStart(2, '0')}`;      
    }
    return null;
}


  static getRouteCellClass(params) {
    const classes = ["gridCell-leftAlign", "routeNameCell"];
    if (params.value === constants.UNRTD_ROUTENAME) {
      classes.push("actionable-cell");
    }
    return classes;
  }

  static getRouteType(params) {
    if (params.data.routeName === constants.UNRTD_ROUTENAME) {
      return "";
    }
    if (params.data.isTrapRoute) {
      return constants.ROUTE_TYPES.TRAP;
    }
    return constants.ROUTE_TYPES.CITY;
  }

  static setRouteType(params) {
    if (params.data.isTrapRoute === params.newValue.value) {
      return false;
    }
    params.data.isTrapRoute = params.newValue.value;
    return true;
  }

  static routeModeRenderer(params) {
    if(params.data.isReturnToTerminal){
      return <ReturnToTerminalIcon width={28} height={28}/>;
    }
    return null;
  }

  static routeModeTooltipRenderer(params) {
    if(params.data.isReturnToTerminal){
      return "Looped Route: Route created to get driver back to terminal (No pickups)";
    }
    return null;
  }

  static getDefaultConfiguration(featureFlags, ROUTENAME_EDITOR_PARAMS) {
    const columnsToHide = [];
    if (featureFlags.DisableReturnToTerminalOption){
      columnsToHide.push("isReturnToTerminal")
    }
    if (!featureFlags.EnableDoorForRoutesGrid){
      columnsToHide.push("door");
    }
    if (!featureFlags.EnableRevenueFieldsOnUI){
      columnsToHide.push("totalRevenue");
    }
    const defaultConfig = {
      title: constants.gridNames.RoutesGrid,
      keyColumn: "routeName",
      rowSelection: "multiple",
      rowDeselection: true,
      defaultColDef: {
        cellEditor: TextFieldEditor,
        sortable: true,
        filter: true,
        resizable: true,
        cellDataType: false,
        suppressKeyboardEvent: suppressKeyboardEventHandler(),
      },
      columns: [
        {
          headerName: "",
          minWidth: constants.columnWidth.VIOLATION_DOT,
          maxWidth: constants.columnWidth.VIOLATION_DOT,
          field: "violation",
          pinned: "left",
          lockPosition: true,
          lockPinned: true,
          cellClass: ["gridCell-centerAlign", "gridCell-dot"],
          filter: false,
          cellRenderer: DotRenderer,
          isInvisibleInShowHide: true,
          gridTitle: constants.gridNames.RoutesGrid,
        },
        {
          headerName: "Route",
          headerTooltip: "Route Name",
          headerClass: "routeNameCell",
          field: "routeName",
          minWidth: constants.columnWidth.ROUTES,
          editable: GridConfig.isNotUnrtdRoute,
          pinned: "left",
          lockPosition: true,
          lockPinned: true,
          cellClass: GridConfig.getRouteCellClass,
          cellStyle: routeNameCellStyling,
          filter: MultiSelectFilter,
          cellEditor: TextFieldEditor,
          cellEditorParams: {
            ...ROUTENAME_EDITOR_PARAMS,
          },
          suppressSizeToFit:true,
        },
        {
          headerName: "Mode",
          headerTooltip : "Mode",
          minWidth: constants.columnWidth.ROUTE_MODE,
          field : "isReturnToTerminal", 
          pinned: "left",
          lockPosition: true,
          lockPinned: true,
          cellClass: "gridCell-leftAlign",
          cellStyle : {
            left : 100
          },
          filter: false,
          hide : true,
          cellRenderer: GridConfig.routeModeRenderer,
          tooltipValueGetter : GridConfig.routeModeTooltipRenderer
        },
        {
          headerName: "Status",
          field: "status",
          minWidth: constants.columnWidth.LOCK_STATUS,
          pinned: "left",
          lockPosition: true,
          lockPinned: true,
          headerTooltip: "Lock Status",
          cellClass: "gridCell-centerAlign",
          cellRenderer: LockStatusRenderer,
          filter: StaticFilter,
          filterParams: {
            staticOptions: Object.values(constants.routeLockStates),
          },
          editable: false,
          cellEditor: DropdownEditor,
          cellEditorPopup: true,
          bulkEditable: GridConfig.isNotUnrtdRoute,
        },
        {
          headerName: "Route Type",
          field: "isTrapRoute",
          minWidth: constants.columnWidth.TOTAL_STOPS,
          headerTooltip: "Route Type",
          filter: StaticFilter,
          filterParams: {
            staticOptions: constants.ROUTE_TYPES_LIST,
          },
          valueFormatter: GridConfig.getRouteType,
          valueSetter: GridConfig.setRouteType,
          filterValueGetter: GridConfig.getRouteType,
          editable: GridConfig.isNotUnrtdRoute,
          cellEditor: DropdownEditor,
          cellEditorPopup: true,
          cellEditorParams: {
            isClearable: false,
            useFormatter: true,
          },
        },
        {
          headerName: "Ttl Stops",
          field: "deliveryStopsCount",
          minWidth: constants.columnWidth.TOTAL_STOPS,
          cellClass: "gridCell-leftAlign",
          headerTooltip: "Total Stops",
          filter: MultiSelectFilter,
          valueGetter: (params) => (params.data.isTrapRoute ? '' : params.data.deliveryStopsCount)
        },
        {
          headerName: "Ttl Bills",
          headerTooltip: "Total Bills",
          field: "prosCount",
          minWidth: constants.columnWidth.TOTAL_BILLS,
          cellClass: "gridCell-leftAlign",
          filter: MultiSelectFilter,
        },
        {
          headerName: "PSE",
          field: "totalPse",
          minWidth: constants.columnWidth.PSE,
          //editable: GridConfig.isEditable,
          headerTooltip: "Pallet Space Equivalent",
          cellClass: "gridCell-leftAlign",
          filter: MultiSelectFilter,
          //tooltipField: "totalPse"
          hide: featureFlags.IsWeightBasedRoutingEnabled,
        },
        {
          headerName: "Calculated PSE",
          field: "totalCalculatedPse",
          minWidth: constants.columnWidth.CALCULATED_PSE,
          headerTooltip: "Calculated Pallet Space Equivalent",
          cellClass: "gridCell-leftAlign",
          filter: MultiSelectFilter,
          hide: true,
        },
        {
          headerName: "Pallets",
          field: "totalPalletCount",
          minWidth: constants.columnWidth.PALLET_COUNT,
          headerTooltip: "Total Pallet Count",
          cellClass: "gridCell-leftAlign",
          filter: MultiSelectFilter,
        },
        {
          headerName: "Weight",
          headerTooltip: "Weight",
          field: "totalWeight",
          minWidth: constants.columnWidth.WEIGHT,
          cellClass: "gridCell-centerAlign",
          filter: MultiSelectFilter,
        },
        {
          headerName: "LG",
          field: "lgProCount",
          minWidth: constants.columnWidth.LG,
          headerTooltip: "Total LG pro count",
          cellClass: "gridCell-leftAlign",
          filter: MultiSelectFilter,
        },
        {
          headerName: "APT",
          field: "aptProCount",
          minWidth: constants.columnWidth.APT,
          headerTooltip: "Total APT pro count",
          cellClass: "gridCell-leftAlign",
          filter: MultiSelectFilter,
        },
        {
          headerName: "Paper DR",
          field: "physicalDrCount",
          minWidth: constants.columnWidth.W_80,
          headerTooltip: "Total Paper DR pros",
          cellClass: "gridCell-leftAlign",
          filter: MultiSelectFilter,
        },
        {
          headerName: "Start Time",
          field: "startTime",
          minWidth: constants.columnWidth.START_TIME,
          headerTooltip: "Start Time",
          cellClass: "gridCell-leftAlign",
          editable: GridConfig.isNotUnrtdOrTrapRoute,
          valueSetter: setTimeFromDateTime,
          cellEditor: DateTimeEditor,
          cellEditorPopup: true,
          cellEditorParams: {
            DATETYPE: constants.DATETYPES.TIME,
          },
          filter: DateTimeFilter,
          filterParams: {
            DATETYPE: constants.DATETYPES.TIME,
          },
        },
        {
          headerName: "Driver",
          field: "driverName",
          minWidth: constants.columnWidth.DRIVER_NAME,
          editable: featureFlags.EnableAssignDriverToRoute,
          headerTooltip: "Driver Name",
          cellClass: "gridCell-leftAlign",
          filter: MultiSelectFilter,
          cellRenderer: DropdownValueCellRenderer,
          cellEditor: DropdownEditor,
          cellEditorPopup: true,
          suppressKeyboardEvent: suppressKeyboardEventHandler(suppressEnter),
          cellEditorParams: {
            isClearable: true,
            editorFieldName:"driverPreference",
          },
        },
        {
          headerName: "Trailer Type",
          field: "trailerName",
          headerTooltip: "Trailer Type",
          editable: GridConfig.isNotUnrtdOrTrapRoute,
          minWidth: constants.columnWidth.TRAILER_TYPE,
          cellClass: "gridCell-leftAlign",
          filter: MultiSelectFilter,
          cellEditor: DropdownEditor,
          cellEditorPopup: true,
          cellRenderer: DropdownValueCellRenderer,
          suppressKeyboardEvent: suppressKeyboardEventHandler(suppressEnter),
        },
        {
          headerName: "Loaded Bills",
          headerTooltip: "Loaded Bills",
          field: "loadedBills",
          minWidth: constants.columnWidth.LOADED_BILLS,
          cellClass: "gridCell-leftAlign",
          filter: MultiSelectFilter,
          valueGetter: (params) => (params.data.isTrapRoute ? '' : params.data.loadedBills)
        },
        {
          headerName: "HU",
          headerTooltip: "Handling Unit",
          field: "totalHu",
          minWidth: constants.columnWidth.HU,
          cellClass: "gridCell-leftAlign",
          filter: MultiSelectFilter,
        },
        {
          headerName: "Cubic Feet",
          headerTooltip: "Cubic Feet",
          field: "cubesCount",
          minWidth: constants.columnWidth.CUBIC_FEET,
          cellClass: "gridCell-centerAlign",
          filter: MultiSelectFilter,
        },
        {
          headerName: "Load Close Time",
          field: "loadCloseTime",
          minWidth: constants.columnWidth.LOADCLOSE_TIME,
          headerTooltip: "Load Close Time",
          // editable: GridConfig.isEditable,
          valueSetter: setTimeFromDateTime,
          cellClass: "gridCell-leftAlign",
          cellEditor: DateTimeEditor,
          cellEditorPopup: true,
          cellEditorParams: {
            DATETYPE: constants.DATETYPES.TIME,
          },
          filter: DateTimeFilter,
          filterParams: {
            DATETYPE: constants.DATETYPES.TIME,
          },
        },
        {
          headerName: "Ttl Miles",
          field: "totalMiles",
          minWidth: constants.columnWidth.TOTAL_MILES,
          cellClass: "gridCell-leftAlign",
          headerTooltip: "Total Miles",
          valueFormatter: roundingNumbers,
          filter: MultiSelectFilter,
        },
        {
          headerName: "Ttl Time",
          field: "totalTime",
          minWidth: constants.columnWidth.TOTAL_TIME,
          headerTooltip: "Total Time",
          cellClass: "gridCell-leftAlign",
          valueGetter: GridConfig.minutesToHours,
          filter: MultiSelectFilter,
        },
        {
          headerName: "Empty Time",
          field: "emptyTime",
          minWidth: constants.columnWidth.EMPTY_TIME,
          headerTooltip: "Start time + total time",
          cellClass: "gridCell-leftAlign",
          cellRenderer: (props) => <EtaEtdRenderer params={props} fieldName="emptyTime" />,
          valueFormatter: formatTime,
          filter: DateTimeFilter,
          filterParams: {
            DATETYPE: constants.DATETYPES.TIME,
          },
        },
        {
          headerName: "Priority",
          field: "priorityProCount",
          minWidth: constants.columnWidth.PRIORITY,
          headerTooltip: "Total GSS pro count",
          cellClass: "gridCell-leftAlign",
          filter: MultiSelectFilter,
        },
        {
          headerName: "Spl Needs",
          field: "specialNeedsProCount",
          minWidth: constants.columnWidth.SPL_NEED,
          headerTooltip: "Total HZ, FZ, FD pros",
          cellClass: "gridCell-leftAlign",
          filter: MultiSelectFilter,
        },
        {
          headerName: "Anchor Point",
          field: "anchorPointName",
          minWidth: constants.columnWidth.ANCHORPOINT_NAME,
          headerTooltip: "Anchor Point",
          cellClass: "gridCell-leftAlign",
          filter: MultiSelectFilter,
          tooltipField: "anchorPointName",
          editable: GridConfig.isNotUnrtdOrTrapRoute,
          cellEditor: DropdownEditor,
          cellEditorPopup: true,
          cellRenderer: DropdownValueCellRenderer,
          suppressKeyboardEvent: suppressKeyboardEventHandler(suppressEnter),
        },
        {
          headerName: "Missed Apts",
          field: "numServiceViolations",
          minWidth: constants.columnWidth.APPOINTMENT,
          headerTooltip: "Missed Apts",
          cellClass: "gridCell-leftAlign",
          filter: MultiSelectFilter,
          valueGetter: (params) => (params.data.isTrapRoute ? '' : params.data.numServiceViolations)
        },
        {
          headerName: "Comments",
          field: "comment",
          minWidth: constants.columnWidth.COMMENTS,
          editable: GridConfig.isNotUnrtdRoute,
          headerTooltip: "Comments",
          cellClass: "gridCell-leftAlign",
          filter: MultiSelectFilter,
          tooltipField: "comment",
        },
        {
          headerName: "AP EmpTime",
          field: "anchorPointEmptyTime",
          minWidth: constants.columnWidth.EMPTY_TIME,
          headerTooltip: "Anchor Point Empty Time",
          cellClass: "gridCell-leftAlign",
          filter: DateTimeFilter,
          filterParams: {
            DATETYPE: constants.DATETYPES.TIME,
          },
          hide: true,
        },
        {
          headerName: 'Door #',
          field: 'door',
          minWidth: constants.columnWidth.W_100,
          headerTooltip: 'Door Number',
          cellClass: 'gridCell-leftAlign',
          filter: MultiSelectFilter,
          editable: GridConfig.isNotUnrtdRoute,
          cellEditor: DropdownEditor,
          cellEditorPopup: true,
          cellRenderer: DropdownValueCellRenderer,
          comparator: customNumericComparator,
          cellEditorParams: {
            isClearable: true,
            useFormatter: false
          }
        },
        {
          headerName: "Trailer #",
          field: "trailerNumber",
          minWidth: constants.columnWidth.W_120,
          editable: GridConfig.isNotUnrtdRoute,
          headerTooltip: "Trailer Number",
          cellClass: "gridCell-leftAlign",
          filter: MultiSelectFilter,
          tooltipField: "Trailer Number",
        },
        {
          headerName: "Ttl Revenue",
          field: "totalRevenue",
          minWidth: constants.columnWidth.W_120,
          tooltipField: "totalRevenue",
          cellClass: "gridCell-leftAlign",
          filter: MultiSelectFilter,
        }
      ],
    };
      defaultConfig.columns = defaultConfig.columns.filter((column) =>
        !columnsToHide.includes(column.field)
      );
    return defaultConfig;
  }
}
